// src/hooks/useAuth.js
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import authApi from '../services/authApi';

export const AuthContext = createContext(null);

export function useAuthProvider() {
  const [consumer, setConsumer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = authApi.getToken();
    if (!token) { setLoading(false); return; }

    authApi.getProfile()
      .then(r => {
        if (r.success && r.data?.consumer) {
          setConsumer(r.data.consumer);
        } else {
          authApi.clearToken();
        }
      })
      .catch(() => authApi.clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await authApi.login(email, password);
    if (r.success) {
      const token = r.token || r.data?.token;
      if (token) {
        authApi.saveToken(token);
        setConsumer(r.consumer || r.data?.consumer || null);
        return { ...r, token };
      }
    }
    return r;
  }, []);

  const register = useCallback(async (data) => {
    const r = await authApi.register(data);
    if (r.success) {
      const token = r.token || r.data?.token;
      if (token) {
        authApi.saveToken(token);
        setConsumer(r.consumer || r.data?.consumer || null);
        return { ...r, token };
      }
    }
    return r;
  }, []);

  const googleLogin = useCallback(async (credential) => {
    const r = await authApi.googleLogin(credential);
    if (r.success) {
      const token = r.token || r.data?.token;
      if (token) {
        authApi.saveToken(token);
        setConsumer(r.consumer || r.data?.consumer || null);
        return { ...r, token };
      }
    }
    return r;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setConsumer(null);
  }, []);

  const isLoggedIn = !!consumer;

  return { consumer, loading, isLoggedIn, login, register, googleLogin, logout, setConsumer };
}

export function useAuth() {
  return useContext(AuthContext);
}
